import React, { useState } from 'react';
import { useNotification } from '../../contexts/NotificationContext';
import NotificationItem from './NotificationItem';
import {
  Bell,
  Filter,
  Check,
  Smartphone,
  X,
  Search
} from 'lucide-react';

const NotificationCenter = ({ isOpen, onClose }) => {
  const {
    notifications,
    unreadCount,
    loading,
    permission,
    requestNotificationPermission,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    filterNotifications,
    getNotificationStats,
    notificationTypes,
    notificationPriorities
  } = useNotification();

  const [activeFilter, setActiveFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPriority, setSelectedPriority] = useState('all');

  const stats = getNotificationStats();

  // Filtrer les notifications
  const getFilteredNotifications = () => {
    let filtered = [...notifications];

    // Filtre par type/statut
    if (activeFilter === 'unread') {
      filtered = filtered.filter(n => !n.read);
    } else if (activeFilter !== 'all') {
      filtered = filtered.filter(n => n.type === activeFilter);
    }

    // Filtre par priorité
    if (selectedPriority !== 'all') {
      filtered = filtered.filter(n => n.priority === selectedPriority);
    }

    // Filtre par recherche
    if (searchQuery !== '') {
      filtered = filtered.filter(n => 
        n.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
        n.message.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }

    // Debug: afficher le filtrage étape par étape
    console.log('🔍 Filtrage étape par étape:', {
      'Étape 1 - Total initial': notifications.length,
      'Étape 2 - Après filtre type/statut': filtered.length,
      'Filtre actif': activeFilter,
      'Priorité sélectionnée': selectedPriority,
      'Recherche': searchQuery,
      'Résultat final': filtered.length
    });

    return filtered;
  };

  const filteredNotifications = getFilteredNotifications();

  // Debug: afficher les filtres dans la console
  console.log('🔍 NotificationCenter Debug:', {
    activeFilter,
    selectedPriority,
    searchQuery,
    totalNotifications: notifications.length,
    filteredCount: filteredNotifications.length,
    allNotificationTypes: notifications.map(n => n.type),
    allNotificationPriorities: notifications.map(n => n.priority),
    filteredNotifications: filteredNotifications.map(n => ({ id: n.id, title: n.title, type: n.type, priority: n.priority, read: n.read }))
  });

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  const handleNotificationAction = (notification) => {
    if (notification.actionUrl) {
      window.location.href = notification.actionUrl;
    }
  };

  const getFilterCount = (filter) => {
    if (filter === 'all') return notifications.length;
    if (filter === 'unread') return unreadCount;
    return filterNotifications({ type: filter }).length;
  };

  const getPriorityCount = (priority) => {
    return filterNotifications({ priority }).length;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-hidden">
      {/* Overlay */}
      <div 
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity duration-300"
        onClick={onClose}
      />
      
      {/* Panel */}
      <div className="absolute right-0 top-0 h-full w-full max-w-md bg-white shadow-xl transform transition-transform duration-300 ease-in-out">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Bell className="w-6 h-6 text-gray-600" />
              {unreadCount > 0 && (
                <div className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {unreadCount > 9 ? '9+' : unreadCount}
                </div>
              )}
            </div>
            <h2 className="text-lg font-semibold text-gray-900">Notifications</h2>
          </div>
          
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors duration-200"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Contrôles */}
        <div className="p-4 border-b border-gray-200 space-y-3">
          {/* Recherche */}
          <div className="relative">
            <input
              type="text"
              placeholder="Rechercher dans les notifications..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent text-sm"
            />
            <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          </div>

          {/* Filtres */}
          <div className="flex items-center gap-2 overflow-x-auto">
            <button
              onClick={() => setActiveFilter('all')}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors duration-200 ${
                activeFilter === 'all' 
                  ? 'bg-primary text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Toutes ({getFilterCount('all')})
            </button>
            
            <button
              onClick={() => setActiveFilter('unread')}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors duration-200 ${
                activeFilter === 'unread' 
                  ? 'bg-primary text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              Non lues ({getFilterCount('unread')})
            </button>

            {Object.entries(notificationTypes).slice(0, 3).map(([key, value]) => (
              <button
                key={key}
                onClick={() => {
                  console.log('🔍 Clic sur type:', { key, value, count: getFilterCount(value) });
                  setActiveFilter(value);
                  // Réinitialiser la priorité quand on change de type
                  setSelectedPriority('all');
                }}
                className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors duration-200 ${
                  activeFilter === value 
                    ? 'bg-primary text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {key.replace(/_/g, ' ').toUpperCase()} ({getFilterCount(value)})
              </button>
            ))}
          </div>

          {/* Filtre par priorité */}
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-600">Priorité:</span>
            <button
              onClick={() => setSelectedPriority('all')}
              className={`px-2 py-1 rounded text-xs font-medium transition-colors duration-200 ${
                selectedPriority === 'all' 
                  ? 'bg-primary text-white' 
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              TOUTES ({notifications.length})
            </button>
            {Object.entries(notificationPriorities).map(([key, value]) => (
              <button
                key={key}
                onClick={() => {
                  console.log('🔍 Clic sur priorité:', { key, value, count: getPriorityCount(value) });
                  setSelectedPriority(value);
                }}
                className={`px-2 py-1 rounded text-xs font-medium transition-colors duration-200 ${
                  selectedPriority === value 
                    ? 'bg-primary text-white' 
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {key} ({getPriorityCount(value)})
              </button>
            ))}
          </div>

          {/* Actions rapides */}
          <div className="flex items-center gap-2">
            <button
              onClick={() => {
                setActiveFilter('all');
                setSelectedPriority('all');
                setSearchQuery('');
                console.log('🔍 Filtres réinitialisés');
              }}
              className="flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors duration-200 text-xs"
            >
              <Filter className="w-3 h-3" />
              Réinitialiser
            </button>
            
            <button
              onClick={handleMarkAllAsRead}
              disabled={unreadCount === 0}
              className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed text-xs"
            >
              <Check className="w-3 h-3" />
              Tout marquer comme lu
            </button>
            
            <button
              onClick={requestNotificationPermission}
              className={`flex items-center gap-1 px-3 py-1 rounded-lg transition-colors duration-200 text-xs ${
                permission === 'granted' 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
            >
              <Smartphone className="w-3 h-3" />
              {permission === 'granted' ? 'Notifications activées' : 'Activer les notifications'}
            </button>
          </div>
        </div>

        {/* Statistiques */}
        <div className="p-4 bg-gray-50 border-b border-gray-200">
          <div className="grid grid-cols-3 gap-4 text-center">
            <div>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
              <p className="text-xs text-gray-600">Total</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-red-600">{stats.unread}</p>
              <p className="text-xs text-gray-600">Non lues</p>
            </div>
            <div>
              <p className="text-2xl font-bold text-green-600">{stats.read}</p>
              <p className="text-xs text-gray-600">Lues</p>
            </div>
          </div>
        </div>

        {/* Liste des notifications */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : filteredNotifications.length > 0 ? (
            <div className="divide-y divide-gray-200">
              {filteredNotifications.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onMarkAsRead={markAsRead}
                  onDelete={deleteNotification}
                  onAction={handleNotificationAction}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-32 text-gray-500 p-4">
              <Bell className="w-8 h-8 mb-2" />
              <p className="text-sm">Aucune notification trouvée</p>
              <p className="text-xs text-gray-400 mt-1">
                Filtre: {activeFilter} | Priorité: {selectedPriority}
              </p>
              <p className="text-xs text-gray-400">
                Total: {notifications.length} | Filtrées: {filteredNotifications.length}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-600">
              {filteredNotifications.length} notification{filteredNotifications.length > 1 ? 's' : ''} affichée{filteredNotifications.length > 1 ? 's' : ''}
            </div>
            <button
              onClick={() => window.location.href = '/notifications'}
              className="text-xs text-primary hover:text-primary/80 transition-colors duration-200"
            >
              Voir toutes →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default NotificationCenter;
